import { useState } from 'react';
import { Building2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LUDOLIO_PRIVACY_POLICY_URL,
  LUDOLIO_TERMS_OF_USE_URL,
} from '@ludolio/shared-ui';
import { AUTH_PORTAL_URL } from '@/config/api';
import { DEMO_EMAIL } from '@/demo/demoData';
import { authApi, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useDemoStore } from '@/stores/demoStore';

const ERROR_BOX = 'rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-950';

export function Login({ redirect }: { redirect: string }) {
  const setAuth = useAuthStore((state) => state.setAuth);
  const enterDemo = useDemoStore((state) => state.enterDemo);
  const [email, setEmail] = useState('');
  const isDemoEmail = email.trim().toLowerCase() === DEMO_EMAIL;
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const target = new URL(redirect, window.location.origin).href;

  const tryDemo = () => {
    const demo = enterDemo();
    setAuth(demo.user);
  };

  const sendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await authApi.startInstitutionalLogin(email, target);
      if (result.action === 'redirect_sso') {
        window.location.href = result.redirectUrl;
        return;
      }
      setIsNewUser(Boolean(result.isNewUser));
      setStep('code');
    } catch (err) {
      setError(getErrorMessage(err, 'We could not send a code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setError('');
    if (isNewUser && (!firstName.trim() || !lastName.trim())) {
      setError('Please enter your first and last name.');
      return;
    }
    setLoading(true);
    try {
      const result = await authApi.verifyOtp(
        email,
        code,
        isNewUser ? firstName.trim() : undefined,
        isNewUser ? lastName.trim() : undefined,
      );
      setAuth({
        ...result.user,
        needsOnboarding: Boolean(result.needsOnboarding || result.needsNameConfirmation),
        needsOrgNoticeConsent: Boolean(result.needsOrgNoticeConsent),
      });
    } catch (err) {
      setError(getErrorMessage(err, 'That code did not work. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const signInWithInstitution = () => {
    const url = new URL(AUTH_PORTAL_URL);
    url.searchParams.set('portal', 'student');
    url.searchParams.set('target', target);
    window.location.href = url.href;
  };

  const backToEmail = () => {
    setStep('email');
    setCode('');
    setFirstName('');
    setLastName('');
    setIsNewUser(false);
    setError('');
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="space-y-5 p-6 text-center sm:p-8">
          <img src="/logo.png" alt="Ludolio" className="mx-auto h-12 w-auto" />
          <div className="space-y-2">
            <CardTitle as="h1" className="text-2xl">
              {step === 'email' ? 'Ludolio Labs' : 'Enter your login code'}
            </CardTitle>
            {step === 'code' && (
              <p className="text-sm text-muted-foreground">We sent a 6-digit code to {email}.</p>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-5 p-6 pt-0 sm:p-8 sm:pt-0">
          {step === 'email' ? (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (isDemoEmail) {
                  tryDemo();
                  return;
                }
                void sendCode();
              }}
            >
              <div className="space-y-2 text-left">
                <Label htmlFor="email">University email address</Label>
                <Input
                  id="email"
                  type="email"
                  className="h-11"
                  placeholder="you@university.edu"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  disabled={loading}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
              {error && <div id="login-error" className={ERROR_BOX} role="alert">{error}</div>}
              {isDemoEmail ? (
                <>
                  <p className="text-center text-xs text-muted-foreground">
                    Demo mode. No verification required. Changes won't be saved.
                  </p>
                  <Button type="button" className="h-11 w-full" onClick={tryDemo}>
                    Try Demo
                  </Button>
                </>
              ) : (
                <Button type="submit" className="h-11 w-full" disabled={loading || !email}>
                  {loading ? 'Checking sign-in…' : 'Continue'}
                </Button>
              )}
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <Button type="button" variant="outline" className="h-11 w-full" disabled={loading} onClick={signInWithInstitution}>
                <Building2 className="h-4 w-4" />
                Sign in with your institution
              </Button>
            </form>
          ) : (
            <form
              className="space-y-4"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                void verifyCode();
              }}
            >
              {isNewUser && (
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" className="h-11" value={firstName} onChange={(event) => setFirstName(event.target.value)} disabled={loading} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" className="h-11" value={lastName} onChange={(event) => setLastName(event.target.value)} disabled={loading} />
                  </div>
                </div>
              )}
              <div className="space-y-2 text-left">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  className="h-11 tracking-[0.3em]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  autoFocus={!isNewUser}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
              {error && <div id="login-error" className={ERROR_BOX} role="alert">{error}</div>}
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={backToEmail} className="text-muted-foreground hover:text-foreground hover:underline">
                  Change email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCode('');
                    void sendCode();
                  }}
                  disabled={loading}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  Resend code
                </button>
              </div>
              <Button type="submit" className="h-11 w-full" disabled={loading || code.length < 6}>
                {loading ? 'Verifying…' : 'Verify & continue'}
              </Button>
            </form>
          )}

          <p className="text-center text-xs text-muted-foreground">
            By logging in, you agree to our{' '}
            <a href={LUDOLIO_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href={LUDOLIO_TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
              Terms of Use
            </a>.
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
